import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { WorkspaceState } from '@/components/workspace/WorkspaceState';
import { useFeatures } from '@/hooks/useFeatures';
import { useFeaturesStore, type FeatureFlag } from '@/stores/features';

/**
 * Страница за флагом функции. Флаги сборки известны сразу; серверный флаг (kubehound)
 * появляется с ответом /v1/features, и до ответа страница ждёт: прямая ссылка и
 * перезагрузка иначе уводили бы на fallback раньше, чем флаг загрузится.
 * На fallback уводит только явное false.
 */
export function FeatureGate({
	flag,
	fallback = '/overview',
	children,
}: {
	flag: FeatureFlag;
	fallback?: string;
	children: ReactNode;
}) {
	const enabled = useFeaturesStore((s) => s.flags[flag]);
	if (enabled === undefined) return <ServerFlagsPending />;
	if (!enabled) return <Navigate to={fallback} replace />;
	return <>{children}</>;
}

/** Серверные флаги ещё не пришли: загрузка, а при сбое запроса -- ошибка с повтором */
function ServerFlagsPending() {
	const { isError, isFetching, refetch } = useFeatures();
	if (isError && !isFetching) {
		return <WorkspaceState state="error" retry={() => void refetch()} />;
	}
	return <WorkspaceState state="loading" />;
}
